import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { RangeSlider } from './range-slider';
import styles from './gun-barrel-azimuth-slider.module.scss';

// A vertical well has no lateral, so its cross-section direction is operator-chosen.
// PROVISIONAL (spec: read the range from the spacing contract): step 3° per INS-11026;
// max 357° = last 3° step below 360° (== 0°). snapAzimuthDeg wraps, not clamps.
export const AZIMUTH_STEP_DEG = 3;
export const AZIMUTH_MIN_DEG = 0;
export const AZIMUTH_MAX_DEG = 357;

/**
 * Bearing a vertical subject opens at (like a horizontal seeding to the heel).
 * Default azimuth is an open product question — change here if it moves.
 */
export const DEFAULT_AZIMUTH_DEG = 0;

/**
 * Clamps a bearing into [0, 357] and snaps it to the nearest 3° step. Bearings
 * at or past a full turn wrap, so 358–360° resolve to 0°.
 * @param {number} deg - Raw bearing in degrees.
 * @return {number} A snapped, in-range bearing.
 */
export function snapAzimuthDeg(deg: number): number {
  if (!Number.isFinite(deg)) return AZIMUTH_MIN_DEG;
  const wrapped = ((deg % 360) + 360) % 360;
  const snapped = Math.round(wrapped / AZIMUTH_STEP_DEG) * AZIMUTH_STEP_DEG;
  return snapped > AZIMUTH_MAX_DEG ? AZIMUTH_MIN_DEG : snapped;
}

interface GunBarrelAzimuthSliderProps {
  /**
   * Committed azimuth in degrees, or null when the user has not chosen one yet.
   * Null is distinct from 0: the BE reads a missing azimuth as "no section
   * direction, nothing to compute" and 0 as due North, so the control must be
   * able to say "unset" rather than defaulting to a real bearing.
   */
  azimuthDeg: number | null;
  /** Commits a snapped azimuth. Debounced 150ms during drag, flushed on release. */
  onChange: (deg: number) => void;
  disabled?: boolean;
}

/**
 * Gun Barrel Azimuth slider — replaces the Reference Point (MD) slider for a
 * vertical subject well. Mirrors `EvalPointMdSlider`'s commit contract: the
 * thumb tracks every tick locally while the expensive parent commit (spacing
 * staleness, cross-section preview) is debounced and flushed on release.
 * @param {GunBarrelAzimuthSliderProps} props - Current azimuth + commit handler.
 * @return {JSX.Element} The azimuth slider control.
 */
export function GunBarrelAzimuthSlider({
  azimuthDeg,
  onChange,
  disabled = false,
}: GunBarrelAzimuthSliderProps) {
  const isUnset = azimuthDeg === null;
  // Park the thumb at the low end while unset. The readout, not the thumb
  // position, is what tells the user nothing is chosen.
  const committedDeg = isUnset ? AZIMUTH_MIN_DEG : snapAzimuthDeg(azimuthDeg);

  // Local drag value so the thumb and the degree readout paint on every tick
  // without waiting on the debounced parent commit.
  const [pendingDeg, setPendingDeg] = useState<number>(committedDeg);
  // Read via ref inside the resync effect so it isn't a dep — as a dep, every
  // drag tick would re-run the effect against the pre-drag value and yank the
  // thumb backwards.
  const pendingDegRef = useRef(pendingDeg);
  pendingDegRef.current = pendingDeg;
  useEffect(() => {
    // Ignore the echo of our own committed snap; resync only on a true external
    // change (initial seed, subject change, data reset).
    if (snapAzimuthDeg(pendingDegRef.current) === committedDeg) return;
    setPendingDeg(committedDeg);
  }, [committedDeg]);

  const commitToParent = useCallback(
    (deg: number) => {
      if (disabled) return;
      onChange(snapAzimuthDeg(deg));
    },
    [disabled, onChange]
  );

  const debouncedCommitToParent = useMemo(
    () => debounce(commitToParent, 150),
    [commitToParent]
  );

  useEffect(
    () => () => debouncedCommitToParent.cancel(),
    [debouncedCommitToParent]
  );

  const handleSliderChange = useCallback(
    (deg: number) => {
      setPendingDeg(deg);
      debouncedCommitToParent(deg);
    },
    [debouncedCommitToParent]
  );

  const handleSliderCommit = useCallback(
    (deg: number) => {
      debouncedCommitToParent.cancel();
      commitToParent(deg);
    },
    [debouncedCommitToParent, commitToParent]
  );

  return (
    <div className={styles.container}>
      <div className={styles.sliderSection}>
        <span className={styles.sliderSectionLabel}>Gun Barrel Azimuth</span>
        <p className={styles.helperText}>
          {/* Not "cross-section direction": the barrel is drawn PERPENDICULAR to
              this bearing (#7125), so naming it the section direction is 90° off.
              No "re-run" hint either — moving the slider re-runs spacing itself. */}
          {isUnset
            ? 'Choose a bearing to run Well Spacing.'
            : 'Aims this vertical well\u2019s gun barrel, which is drawn perpendicular to the bearing. Spacing re-runs when you move the slider.'}
        </p>
        <RangeSlider
          min={AZIMUTH_MIN_DEG}
          max={AZIMUTH_MAX_DEG}
          step={AZIMUTH_STEP_DEG}
          value={pendingDeg}
          onChange={handleSliderChange}
          onCommit={handleSliderCommit}
          minLabel={`${AZIMUTH_MIN_DEG}°`}
          maxLabel={`${AZIMUTH_MAX_DEG}°`}
          disabled={disabled}
        />
        <div className={styles.selectedValue}>
          {isUnset ? 'Not set' : `${pendingDeg}°`}
        </div>
      </div>
    </div>
  );
}
